import { useQueryClient } from '@tanstack/react-query';
import useAppMutation from 'hooks/useAppMutation';
import useNotify from 'hooks/useNotify';
import { GetPropertyOwnerResponseTO, PropertyOwner } from 'models/Owner';
import { useTranslation } from 'react-i18next';
import API from 'services/API';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { PROPERTY_OWNERS_BASE_QUERY_KEY } from '../../usePropertyOwners';

export type OwnerPayload =
  | (Pick<GetPropertyOwnerResponseTO['propertyOwnership'], 'propertyUid'> & {
      owner: Partial<
        Omit<
          PropertyOwner,
          'uid' | 'lastLoginUtcDateTime' | 'invitationSent'
        > & {
          uid?: string;
        }
      >;
      collectAndRemitTaxes?: boolean;
      copyOwnerWhenBookingConfirmed?: boolean;
    })
  | {
      owner: {
        email: string;
      };
    };

const ownerMutationKey = 'create-or-update-owner';

const useOwnerMutations = () => {
  const { notifyError } = useNotify();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { mutateAsync: createOrUpdateOwner, isLoading: isSubmitting } =
    useAppMutation(
      [ownerMutationKey],
      ({
        propertyUid,
        payload,
      }: {
        propertyUid: string;
        payload: OwnerPayload;
        successCallback?: () => void;
      }) => {
        return API.patch(
          `/api/v3.2/property-ownership/${propertyUid}`,
          payload,
        );
      },
      {
        onSuccess: (_, { propertyUid, successCallback }) => {
          queryClient.resetQueries({
            queryKey: [PROPERTY_OWNERS_BASE_QUERY_KEY, 'detail', propertyUid],
          });

          successCallback?.();
        },
        onError: (error: any) => {
          notifyError(
            getErrorMessage({
              apiErrorCode: error.response.data?.apiErrorCode,
              apiErrorMessage: error.response.data?.apiErrorMessage,
              baseKey: 'pageProperty.apiErrorCodes.',
              t,
            }),
          );
        },
      },
    );

  return {
    createOrUpdateOwner,
    isSubmitting,
  };
};

export default useOwnerMutations;
